import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import HomeIcon from "@mui/icons-material/Home";
import Stepper from "./Stepper";
import Step1BasicSettings from "./Step1BasicSettings";
import Step2LicenseInfo from "./Step2LicenseInfo";
import Step3Lokalita from "./Step3Lokalita";
import Step4Kiosky from "./Step4Kiosky";
import { validateStep1 } from "./validateStep1";
import { validateStep2 } from "./validateStep2";
import { validateStep3 } from "./validateStep3";
import { validateStep4 } from "./validateStep4";
import { BACKEND_DOMAIN_PRODUCTION } from "./step2Options";
import {
  registerDSOKCustomer,
  lookupCompanyByIco,
} from "app/services/api/posCustomerApi";

const DEFAULT_STEP1_VALUES = {
  active: true,
  companyName: "",
  storeCode: "",
  street: "",
  city: "",
  zip: "",
  country: "sk",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const DEFAULT_STEP2_VALUES = {
  beDomain: "",
  frontendType: "",
  businessPartner: "",
  servicePartner: "none",
  typLicencie: "",
  relationsTypZakaznika: "",
  posIntegracia: "none",
};

/** Static defaults for Step 2 (applied only on first entry to Step 2). */
const STEP2_STATIC_DEFAULTS = {
  beDomain: BACKEND_DOMAIN_PRODUCTION,
  frontendType: "easyFood",
  businessPartner: "",
  servicePartner: "none",
  typLicencie: "basic",
  relationsTypZakaznika: "standalone",
  posIntegracia: "none",
};

const DEFAULT_STEP3_VALUES = {
  locationName: "",
  ico: "",
  dic: "",
  icDph: "",
  street: "",
  zip: "",
  city: "",
  firstName: "",
  lastName: "",
  phone: "",
};

const DEFAULT_STEP4_VALUES = {
  deviceCount: "",
  posProvider: "",
  ekasaProvider: "",
};

/**
 * Poskladá zrozumiteľnú chybovú hlášku pri zlyhaní registrácie zákazníka.
 * Backend okrem generickej "message" vracia aj konkrétny "reason" (prečo to padlo)
 * a "step" (v ktorom kroku) – bez nich by používateľ videl len status bez príčiny.
 */
const buildRegistrationErrorMessage = (data, fallback) => {
  const base = data?.message || fallback;
  const reason = data?.reason;
  const step = data?.step;
  if (!reason) return step ? `${base} (krok: ${step})` : base;
  return step ? `${base}\n${reason}\n(krok: ${step})` : `${base}\n${reason}`;
};

const CreateCustomerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Values, setStep1Values] = useState(DEFAULT_STEP1_VALUES);
  const [step1Errors, setStep1Errors] = useState({});
  const [step2Values, setStep2Values] = useState(DEFAULT_STEP2_VALUES);
  const [step2Errors, setStep2Errors] = useState({});
  const [step3Values, setStep3Values] = useState(DEFAULT_STEP3_VALUES);
  const [step3Errors, setStep3Errors] = useState({});
  const [step4Values, setStep4Values] = useState(DEFAULT_STEP4_VALUES);
  const [step4Errors, setStep4Errors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const step2DefaultsAppliedRef = useRef(false);

  // Prefill the form from the company register by IČO (name + address → Step 1,
  // IČO/DIČ/IČ DPH + address → Step 3). Same source as datavision-crm (ORSF register).
  const handleIcoLookup = useCallback(
    async (ico) => {
      const q = String(ico || "").trim();
      if (!q || lookupBusy) return;
      setLookupBusy(true);
      try {
        const { data } = await lookupCompanyByIco(q);
        const c = data?.company;
        if (!c) {
          enqueueSnackbar(
            t("pages.customersNew.icoNotFound", {
              defaultValue: "IČO sa v registri nenašlo.",
            }),
            { variant: "error" },
          );
          return;
        }
        setStep1Values((prev) => ({
          ...prev,
          companyName: c.name || prev.companyName,
          street: c.street || prev.street,
          city: c.city || prev.city,
          zip: c.zip || prev.zip,
          country: (c.countryCode || prev.country || "sk").toLowerCase(),
        }));
        setStep3Values((prev) => ({
          ...prev,
          ico: c.ico || q,
          dic: c.dic || prev.dic,
          icDph: c.icDph || prev.icDph,
          street: c.street || prev.street,
          city: c.city || prev.city,
          zip: c.zip || prev.zip,
          locationName: prev.locationName || c.name || "",
        }));
        if (data?.warning) {
          enqueueSnackbar(data.warning, {
            variant: "warning",
            style: { whiteSpace: "pre-line" },
          });
        } else {
          enqueueSnackbar(
            t("pages.customersNew.icoPrefilled", {
              defaultValue: "Údaje predvyplnené z registra.",
            }),
            { variant: "success" },
          );
        }
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.message ||
            t("pages.customersNew.icoLookupFailed", {
              defaultValue: "Načítanie z registra zlyhalo.",
            }),
          { variant: "error" },
        );
      } finally {
        setLookupBusy(false);
      }
    },
    [lookupBusy, enqueueSnackbar, t],
  );

  useEffect(() => {
    if (currentStep !== 2) return;
    if (step2DefaultsAppliedRef.current) return;
    step2DefaultsAppliedRef.current = true;
    setStep2Values((prev) => ({ ...prev, ...STEP2_STATIC_DEFAULTS }));
  }, [currentStep]);

  const handleStep1Change = useCallback((field, value) => {
    setStep1Values((prev) => ({ ...prev, [field]: value }));
    setStep1Errors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleStep2Change = useCallback((field, value) => {
    setStep2Values((prev) => ({ ...prev, [field]: value }));
    setStep2Errors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleStep3Change = useCallback((field, value) => {
    setStep3Values((prev) => ({ ...prev, [field]: value }));
    setStep3Errors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleStep4Change = useCallback((field, value) => {
    setStep4Values((prev) => ({ ...prev, [field]: value }));
    setStep4Errors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep === 1) {
      const errors = validateStep1(step1Values);
      const hasErrors = Object.values(errors).some((msg) => msg);
      if (hasErrors) {
        setStep1Errors(errors);
        return;
      }
      setStep1Errors({});
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const errors = validateStep2(step2Values);
      const hasErrors = Object.values(errors).some((msg) => msg);
      if (hasErrors) {
        setStep2Errors(errors);
        return;
      }
      setStep2Errors({});
      setCurrentStep(3);
    } else if (currentStep === 3) {
      const errors = validateStep3(step3Values);
      const hasErrors = Object.values(errors).some((msg) => msg);
      if (hasErrors) {
        setStep3Errors(errors);
        return;
      }
      setStep3Errors({});
      setCurrentStep(4);
    }
  }, [currentStep, step1Values, step2Values, step3Values]);

  const handleBack = useCallback(() => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 4) {
      setCurrentStep(3);
    }
  }, [currentStep]);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex > currentStep) return;
      if (stepIndex === currentStep) return;
      setCurrentStep(stepIndex);
    },
    [currentStep],
  );

  const handleCreateCustomer = useCallback(async () => {
    const errors = validateStep4(step4Values);
    const hasErrors = Object.values(errors).some((msg) => msg);
    if (hasErrors) {
      setStep4Errors(errors);
      return;
    }
    setStep4Errors({});
    const payload = {
      step1: { ...step1Values },
      step2: { ...step2Values },
      step3: { ...step3Values },
      step4: { ...step4Values },
    };
    setLoading(true);
    try {
      const response = await registerDSOKCustomer(payload);
      const data = response?.data;
      const isSuccess =
        data?.ok === true ||
        data?.code === 201 ||
        data?.status === "Success" ||
        response?.status === 201;
      if (isSuccess) {
        enqueueSnackbar(
          t("pages.customersNew.registerSuccess", {
            defaultValue: "Customer registered successfully",
          }),
          { variant: "success" },
        );
        navigate("/dsok/customers");
        return;
      }
      enqueueSnackbar(
        buildRegistrationErrorMessage(
          data,
          t("pages.customersNew.registerFailed", {
            defaultValue: "Customer registration failed",
          }),
        ),
        { variant: "error", style: { whiteSpace: "pre-line" } },
      );
    } catch (err) {
      enqueueSnackbar(
        buildRegistrationErrorMessage(
          err?.response?.data,
          t("pages.customersNew.registerError", {
            defaultValue: "Unexpected error during registration",
          }),
        ),
        { variant: "error", style: { whiteSpace: "pre-line" } },
      );
    } finally {
      setLoading(false);
    }
  }, [
    step1Values,
    step2Values,
    step3Values,
    step4Values,
    enqueueSnackbar,
    navigate,
    t,
  ]);

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBreadcrumbClick("/");
          }}
          sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          {t("sidebar.menuItem.home")}
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBreadcrumbClick("/dsok/customers");
          }}
          sx={{ textDecoration: "none" }}
        >
          {t("sidebar.menuItem.customers")} - DSOK
        </Link>
        <Typography color="text.primary">Nový zákazník</Typography>
      </Breadcrumbs>

      <Stepper activeStep={currentStep} onStepClick={handleStepClick} />

      <Box sx={{ mb: 3 }}>
        {currentStep === 1 && (
          <Step1BasicSettings
            values={step1Values}
            errors={step1Errors}
            onChange={handleStep1Change}
            onLookup={handleIcoLookup}
            lookupBusy={lookupBusy}
          />
        )}
        {currentStep === 2 && (
          <Step2LicenseInfo
            values={step2Values}
            errors={step2Errors}
            onChange={handleStep2Change}
          />
        )}
        {currentStep === 3 && (
          <Step3Lokalita
            values={step3Values}
            errors={step3Errors}
            onChange={handleStep3Change}
          />
        )}
        {currentStep === 4 && (
          <Step4Kiosky
            values={step4Values}
            errors={step4Errors}
            onChange={handleStep4Change}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: 1,
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {currentStep === 1 && (
          <Button variant="contained" color="primary" onClick={handleNext}>
            Ďalej
          </Button>
        )}
        {currentStep === 2 && (
          <>
            <Button variant="outlined" onClick={handleBack}>
              Naspäť
            </Button>
            <Button variant="contained" color="primary" onClick={handleNext}>
              Ďalej
            </Button>
          </>
        )}
        {currentStep === 3 && (
          <>
            <Button variant="outlined" onClick={handleBack}>
              Naspäť
            </Button>
            <Button variant="contained" color="primary" onClick={handleNext}>
              Ďalej
            </Button>
          </>
        )}
        {currentStep === 4 && (
          <>
            <Button variant="outlined" onClick={handleBack}>
              Naspäť
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateCustomer}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {loading
                ? t("common.saving", { defaultValue: "Ukladá sa…" })
                : "Vytvoriť zákazníka"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CreateCustomerPage;
